import React, { useState, useMemo } from 'react';
import {
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend
} from 'recharts';
import { Panel, Chip, Btn, PageHead, I } from '../components/hud';

// Sliding window in seconds used to compute DPS
const DPS_WINDOW = 1;

// Friendly labels + descriptions for each parsed event kind, shown in the settings dialog.
const KIND_LABELS = {
  damage_out: { label: 'Outgoing damage', desc: 'Damage you dealt to targets' },
  damage_in: { label: 'Incoming damage', desc: 'Damage taken from attackers' },
  miss_out: { label: 'Misses (outgoing)', desc: 'Your weapons that missed' },
  neut: { label: 'Energy neut (outgoing)', desc: 'GJ you neutralized on targets' },
  neut_in: { label: 'Energy neut (incoming)', desc: 'GJ drained from your modules' },
  bounty: { label: 'Bounty payouts', desc: 'ISK bounty notifications' },
  bounty_other: { label: 'Bounty (other)', desc: 'Other bounty messages' },
  jump: { label: 'System jumps', desc: 'Jump gate transitions' },
  notify: { label: 'Ship notifications', desc: 'Ship status, module messages, etc.' },
  generic: { label: 'Generic events', desc: 'Other Generic-category messages' },
  combat_other: { label: 'Combat (other)', desc: 'Combat lines that did not match a known pattern' },
  other: { label: 'Other', desc: 'Unrecognized event types' }
};

// Kinds that are noisy / span large idle gaps — disabled by default to keep the timeline tight.
const DEFAULT_DISABLED_KINDS = ['notify'];

// Damage quality / hit type weights for display ordering
const QUALITIES = ['Misses', 'Grazes', 'Glances Off', 'Hits', 'Penetrates', 'Smashes', 'Wrecks'];

const QUALITY_COLORS = {
  Misses: '#666666',
  Grazes: '#9ba4b0',
  'Glances Off': '#5c93a8',
  Hits: '#33b5e5',
  Penetrates: '#ffb547',
  Smashes: '#ff8a65',
  Wrecks: '#ff5b6a'
};

// Best-effort weapon -> primary damage type mapping (EVE Online)
// The text log doesn't expose damage type directly, so we infer it from the weapon name.
const WEAPON_DAMAGE_TYPE = [
  { match: /entropic disintegrator/i, type: 'Thermal/EM' },
  { match: /pulse laser|beam laser|tachyon|dual heavy pulse|mega pulse|focused medium pulse|gatling pulse/i, type: 'EM/Thermal' },
  { match: /autocannon|artillery|howitzer|repeater|carbine/i, type: 'Explosive/Kinetic' },
  { match: /railgun|blaster|neutron|electron|ion|hybrid/i, type: 'Kinetic/Thermal' },
  { match: /missile|rocket|torpedo|launcher|fury|rage|scourge|inferno|mjolnir|nova/i, type: 'Mixed (missile)' },
  { match: /drone/i, type: 'Drone' }
];

const inferDamageType = (weapon) => {
  if (!weapon) return 'Unknown';
  for (const entry of WEAPON_DAMAGE_TYPE) {
    if (entry.match.test(weapon)) return entry.type;
  }
  return 'Unknown';
};

// Parse "HH:MM:SS" -> seconds since start of day
const timeToSeconds = (hms) => {
  const [h, m, s] = hms.split(':').map(Number);
  return h * 3600 + m * 60 + s;
};

const formatNumber = (n) => n.toLocaleString();

const formatISK = (n) => {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(2) + 'K';
  return formatNumber(n);
};

// Real EVE Gamelog combat lines wrap ship/pilot names in nested <color>/<font>/<b>
// tags. Strip them so the inner pattern matchers can see plain text.
const stripFormattingTags = (s) =>
  s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

/**
 * Parse a single log line. Two formats are recognised:
 *   - Bracketed (real Gamelog): "[ YYYY.MM.DD HH:MM:SS ] (category) Message"
 *   - Legacy tab-delimited:     "HH:MM:SS\tCategory\tMessage"
 * Returns a structured event or null if the line cannot be parsed (header /
 * separator lines fall through here).
 */
const parseLine = (line) => {
  const trimmed = line.replace(/\r/g, '').trimEnd();
  if (!trimmed) return null;

  let time;
  let category;
  let rest;

  const bracket = trimmed.match(/^\[\s*\d{4}\.\d{2}\.\d{2}\s+(\d{2}:\d{2}:\d{2})\s*\]\s*\(([^)]+)\)\s*(.*)$/);
  if (bracket) {
    time = bracket[1];
    // Bracketed format uses lowercase categories like (combat), (notify) — normalize
    // to the same casing the rest of the parser expects (e.g. "Combat", "Notify").
    const raw = bracket[2];
    category = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    rest = stripFormattingTags(bracket[3]);
  } else {
    const tab = trimmed.match(/^(\d{2}:\d{2}:\d{2})[\s\t]+(\w+)[\s\t]+(.*)$/);
    if (!tab) return null;
    time = tab[1];
    category = tab[2];
    rest = tab[3];
  }

  const base = { time, seconds: timeToSeconds(time), category, raw: rest };

  if (category === 'Combat') {
    // Outgoing: "<amount> to <target> - <weapon> - <quality>"
    let m = rest.match(/^(\d+)\s+to\s+(.+?)\s+-\s+(.+?)\s+-\s+(\w[\w\s]*)$/);
    if (m) {
      return {
        ...base,
        kind: 'damage_out',
        amount: Number(m[1]),
        target: m[2].trim(),
        weapon: m[3].trim(),
        quality: m[4].trim(),
        damageType: inferDamageType(m[3])
      };
    }
    // Incoming: "<amount> from <source> - <quality>"
    m = rest.match(/^(\d+)\s+from\s+(.+?)\s+-\s+(\w[\w\s]*)$/);
    if (m) {
      return {
        ...base,
        kind: 'damage_in',
        amount: Number(m[1]),
        source: m[2].trim(),
        quality: m[3].trim()
      };
    }
    // Outgoing miss: "Your <weapon> misses <target> completely - <weapon>"
    m = rest.match(/^Your\s+(.+?)\s+misses\s+(.+?)\s+completely\s+-\s+.+$/i);
    if (m) {
      return {
        ...base,
        kind: 'miss_out',
        amount: 0,
        target: m[2].trim(),
        weapon: m[1].trim(),
        quality: 'Misses',
        damageType: inferDamageType(m[1])
      };
    }
    // Energy neutralization. Format: "<n> GJ energy neutralized <X> - <Y>".
    //   When <X> begins with "your"/"you", it names YOUR weapon casting the neut →
    //     outgoing, with <Y> as the target.
    //   Otherwise <X> is the module on your ship that got drained → incoming, with
    //     <Y> as the attacker.
    m = rest.match(/^(\d+)\s+GJ\s+energy\s+neutralized\s+(.+?)\s+-\s+(.+)$/i);
    if (m) {
      const first = m[2].trim();
      const second = m[3].trim();
      const isOutgoing = /^your\b/i.test(first) || /^you\b/i.test(first);
      return {
        ...base,
        kind: isOutgoing ? 'neut' : 'neut_in',
        amount: Number(m[1]),
        target: isOutgoing ? second : null,
        source: isOutgoing ? null : second,
        module: isOutgoing ? first.replace(/^your\s+/i, '') : first
      };
    }
    return { ...base, kind: 'combat_other' };
  }

  if (category === 'Bounty') {
    const m = rest.match(/^([\d\s.,]+)\s+ISK/i);
    if (m) {
      const amount = Number(m[1].replace(/[\s.,]/g, ''));
      return { ...base, kind: 'bounty', amount };
    }
    return { ...base, kind: 'bounty_other' };
  }

  if (category === 'Generic') {
    const jump = rest.match(/^Jumping from\s+(\S+)\s+to\s+(\S+)/i);
    if (jump) {
      return { ...base, kind: 'jump', from: jump[1], to: jump[2] };
    }
    return { ...base, kind: 'generic' };
  }

  if (category === 'Notify') {
    return { ...base, kind: 'notify', message: rest };
  }

  return { ...base, kind: 'other' };
};

const parseLog = (text) => {
  return text
    .split(/\n/)
    .map(parseLine)
    .filter(Boolean);
};

/**
 * Build a per-second damage timeline starting from t0.
 * Returns an array { t, label, dpsOut, dpsIn, dmgOut, dmgIn } where
 * dps* are computed across the trailing DPS_WINDOW seconds.
 */
const buildTimeline = (events) => {
  if (events.length === 0) return [];
  const seconds = events.map((e) => e.seconds);
  const t0 = Math.min(...seconds);
  const t1 = Math.max(...seconds);
  const span = t1 - t0;
  if (span < 0) return [];

  const outBySec = new Map();
  const inBySec = new Map();
  const neutOutBySec = new Map();
  const neutInBySec = new Map();
  for (const e of events) {
    const s = e.seconds - t0;
    if (e.kind === 'damage_out') {
      outBySec.set(s, (outBySec.get(s) || 0) + e.amount);
    } else if (e.kind === 'damage_in') {
      inBySec.set(s, (inBySec.get(s) || 0) + e.amount);
    } else if (e.kind === 'neut') {
      neutOutBySec.set(s, (neutOutBySec.get(s) || 0) + e.amount);
    } else if (e.kind === 'neut_in') {
      neutInBySec.set(s, (neutInBySec.get(s) || 0) + e.amount);
    }
  }

  const result = [];
  for (let s = 0; s <= span; s++) {
    let outSum = 0;
    let inSum = 0;
    let neutOutSum = 0;
    let neutInSum = 0;
    for (let k = Math.max(0, s - DPS_WINDOW + 1); k <= s; k++) {
      outSum += outBySec.get(k) || 0;
      inSum += inBySec.get(k) || 0;
      neutOutSum += neutOutBySec.get(k) || 0;
      neutInSum += neutInBySec.get(k) || 0;
    }
    const totalSec = new Date((t0 + s) * 1000).toISOString().substr(11, 8);
    // DPS values use null on idle seconds so the chart can connectNulls and bridge
    // across gaps; NEUT values stay numeric so the lines drop to 0 between events.
    result.push({
      t: s,
      label: totalSec,
      dpsOut: outSum > 0 ? +(outSum / DPS_WINDOW).toFixed(1) : null,
      dpsIn: inSum > 0 ? +(inSum / DPS_WINDOW).toFixed(1) : null,
      neutOut: +(neutOutSum / DPS_WINDOW).toFixed(1),
      neutIn: +(neutInSum / DPS_WINDOW).toFixed(1),
      dmgOut: outBySec.get(s) || 0,
      dmgIn: inBySec.get(s) || 0
    });
  }
  return result;
};

/**
 * Build per-hit scatter data for the DPS chart overlay.
 * Each entry preserves the metadata needed by the tooltip (quality, weapon, target/source).
 */
const buildHits = (events) => {
  if (events.length === 0) return { out: [], in: [] };
  const t0 = Math.min(...events.map((e) => e.seconds));
  const out = [];
  const incoming = [];
  for (const e of events) {
    if (e.kind === 'damage_out') {
      out.push({
        t: e.seconds - t0,
        amount: e.amount,
        quality: e.quality,
        weapon: e.weapon,
        target: e.target
      });
    } else if (e.kind === 'damage_in') {
      incoming.push({
        t: e.seconds - t0,
        amount: e.amount,
        quality: e.quality,
        source: e.source
      });
    }
  }
  return { out, in: incoming };
};

const secondsOfDayToHMS = (s) => new Date(s * 1000).toISOString().substr(11, 8);

const ChartTooltip = ({ active, payload, t0 }) => {
  if (!active || !payload || payload.length === 0) return null;
  const t = payload[0]?.payload?.t;
  const timeLabel = t !== undefined && t0 !== undefined ? secondsOfDayToHMS(t0 + t) : '';
  return (
    <div style={{
      backgroundColor: '#0a1018',
      border: '1px solid rgba(51,181,229,0.3)',
      padding: '8px 12px',
      fontFamily: 'var(--font-mono)',
      fontSize: 11
    }}>
      <div style={{ color: 'var(--text-3)', marginBottom: 4, letterSpacing: '0.1em' }}>{timeLabel}</div>
      {payload.map((p, i) => {
        const isHit = p.name === 'Outgoing hit' || p.name === 'Incoming hit';
        if (isHit) {
          const d = p.payload;
          const partner = p.name === 'Outgoing hit' ? d.target : d.source;
          const arrow = p.name === 'Outgoing hit' ? '→' : '←';
          return (
            <div key={i} style={{ color: p.color || p.fill, marginBottom: 2 }}>
              {p.name}: <strong>{formatNumber(d.amount)}</strong> · {d.quality}
              {d.weapon ? ` · ${d.weapon}` : ''}
              {partner ? ` ${arrow} ${partner}` : ''}
            </div>
          );
        }
        return (
          <div key={i} style={{ color: p.color, marginBottom: 2 }}>
            {p.name}: {p.value}
          </div>
        );
      })}
    </div>
  );
};

const aggregate = (events) => {
  let totalOut = 0;
  let totalIn = 0;
  let shotsFired = 0;
  let hits = 0;
  const byTarget = new Map();
  const bySource = new Map();
  const byQualityOut = new Map();
  const byQualityIn = new Map();
  const byWeapon = new Map();
  const byDamageType = new Map();
  let bountyTotal = 0;
  const jumps = [];
  const notifies = [];

  for (const e of events) {
    if (e.kind === 'damage_out') {
      totalOut += e.amount;
      shotsFired++;
      hits++;
      byTarget.set(e.target, (byTarget.get(e.target) || 0) + e.amount);
      byQualityOut.set(e.quality, (byQualityOut.get(e.quality) || 0) + e.amount);
      byWeapon.set(e.weapon, (byWeapon.get(e.weapon) || 0) + e.amount);
      byDamageType.set(e.damageType, (byDamageType.get(e.damageType) || 0) + e.amount);
    } else if (e.kind === 'miss_out') {
      shotsFired++;
      byQualityOut.set('Misses', (byQualityOut.get('Misses') || 0) + 1);
    } else if (e.kind === 'damage_in') {
      totalIn += e.amount;
      bySource.set(e.source, (bySource.get(e.source) || 0) + e.amount);
      byQualityIn.set(e.quality, (byQualityIn.get(e.quality) || 0) + e.amount);
    } else if (e.kind === 'bounty') {
      bountyTotal += e.amount;
    } else if (e.kind === 'jump') {
      jumps.push(e);
    } else if (e.kind === 'notify') {
      notifies.push(e);
    }
  }

  const seconds = events.map((e) => e.seconds);
  const span = seconds.length > 0 ? Math.max(...seconds) - Math.min(...seconds) + 1 : 0;
  const avgDpsOut = span > 0 ? totalOut / span : 0;
  const avgDpsIn = span > 0 ? totalIn / span : 0;

  return {
    totalOut,
    totalIn,
    shotsFired,
    hits,
    accuracy: shotsFired > 0 ? (hits / shotsFired) * 100 : 0,
    byTarget: [...byTarget.entries()]
      .map(([name, dmg]) => ({ name, dmg }))
      .sort((a, b) => b.dmg - a.dmg),
    bySource: [...bySource.entries()]
      .map(([name, dmg]) => ({ name, dmg }))
      .sort((a, b) => b.dmg - a.dmg),
    byQualityOut: [...byQualityOut.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => QUALITIES.indexOf(a.name) - QUALITIES.indexOf(b.name)),
    byQualityIn: [...byQualityIn.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => QUALITIES.indexOf(a.name) - QUALITIES.indexOf(b.name)),
    byWeapon: [...byWeapon.entries()].map(([name, dmg]) => ({ name, dmg })),
    byDamageType: [...byDamageType.entries()].map(([name, value]) => ({ name, value })),
    bountyTotal,
    jumps,
    notifies,
    span,
    avgDpsOut,
    avgDpsIn
  };
};

const SAMPLE_LOG = `------------------------------------------------------------
  Gamelog
  Listener: Sample Pilot
  Session Started: 2026.04.28 08:55:00
------------------------------------------------------------
[ 2026.04.28 08:55:42 ] (combat) 2285 from Sansha's Dreadnought - Smashes
[ 2026.04.28 08:55:43 ] (combat) 18548 to Sansha's Dreadnought - Zorya's Supratidal Entropic Disintegrator - Smashes
[ 2026.04.28 08:56:12 ] (combat) 2464 to Sansha's Hellhound - Zorya's Supratidal Entropic Disintegrator - Penetrates
[ 2026.04.28 08:56:13 ] (bounty) 39 166 ISK added to next bounty payout (payment adjusted)
[ 2026.04.28 08:56:17 ] (combat) 1977 to Sansha's Fiend - Zorya's Supratidal Entropic Disintegrator - Smashes
[ 2026.04.28 08:56:19 ] (combat) 1962 to Sansha's Fiend - Zorya's Supratidal Entropic Disintegrator - Hits
[ 2026.04.28 08:56:20 ] (bounty) 36 666 ISK added to next bounty payout (payment adjusted)
[ 2026.04.28 09:02:55 ] (generic) Jumping from GE-8JV to V-3YG7
[ 2026.04.28 09:03:45 ] (generic) Jumping from V-3YG7 to HY-RWO
[ 2026.04.28 09:25:50 ] (notify) Ship stopping
[ 2026.04.28 09:39:43 ] (notify) Ship stopping`;

// ── KIND metadata for the event timeline rows ────────────────────────────────
const KIND_META = {
  damage_out:   { color: '#5bd99a', label: 'OUT' },
  damage_in:    { color: '#ff5b6a', label: 'IN' },
  miss_out:     { color: '#56697d', label: 'MISS' },
  neut:         { color: '#e065ff', label: 'NEUT OUT' },
  neut_in:      { color: '#ff8a65', label: 'NEUT IN' },
  bounty:       { color: '#ffb547', label: 'ISK' },
  bounty_other: { color: '#ffb547', label: 'ISK' },
  jump:         { color: '#33b5e5', label: 'JUMP' },
  notify:       { color: '#56697d', label: 'NOTIFY' },
  generic:      { color: '#56697d', label: 'GEN' },
  combat_other: { color: '#56697d', label: 'CMB' },
  other:        { color: '#56697d', label: '···' }
};

const renderEventBody = (e) => {
  switch (e.kind) {
    case 'damage_out':
      return (
        <span>
          <strong style={{ color: '#5bd99a' }}>{formatNumber(e.amount)}</strong>
          <span style={{ color: 'var(--text-2)' }}> → </span>
          {e.target}
          <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{e.weapon} · {e.quality}</span>
        </span>
      );
    case 'damage_in':
      return (
        <span>
          <strong style={{ color: '#ff5b6a' }}>{formatNumber(e.amount)}</strong>
          <span style={{ color: 'var(--text-2)' }}> ← </span>
          {e.source}
          <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{e.quality}</span>
        </span>
      );
    case 'miss_out':
      return (
        <span>
          <span style={{ color: 'var(--text-3)' }}>missed</span>
          <span style={{ color: 'var(--text-2)', margin: '0 6px' }}>→</span>
          {e.target}
          <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>{e.weapon}</span>
        </span>
      );
    case 'neut':
      return (
        <span>
          <strong style={{ color: '#e065ff' }}>{formatNumber(e.amount)} GJ</strong>
          <span style={{ color: 'var(--text-2)', margin: '0 6px' }}>neutralized</span>
          {e.target}
        </span>
      );
    case 'neut_in':
      return (
        <span>
          <strong style={{ color: '#ff8a65' }}>{formatNumber(e.amount)} GJ</strong>
          <span style={{ color: 'var(--text-2)', margin: '0 6px' }}>drained</span>
          {e.module ? <em>{e.module}</em> : 'cap'}
          <span style={{ color: 'var(--text-2)', margin: '0 6px' }}>from</span>
          {e.source}
        </span>
      );
    case 'bounty':
      return (
        <span>
          <strong style={{ color: '#ffb547' }}>+{formatISK(e.amount)} ISK</strong>
          <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>bounty</span>
        </span>
      );
    case 'jump':
      return (
        <span>
          <span style={{ color: 'var(--text-2)' }}>Jumping</span>
          <strong style={{ marginLeft: 6 }}>{e.from}</strong>
          <span style={{ color: 'var(--accent)', margin: '0 6px' }}>→</span>
          <strong>{e.to}</strong>
        </span>
      );
    case 'notify':
      return <span style={{ color: 'var(--text-2)' }}>{e.message}</span>;
    default:
      return <span style={{ color: 'var(--text-2)' }}>{e.raw}</span>;
  }
};

const TimelineRow = ({ event }) => {
  const meta = KIND_META[event.kind] || KIND_META.other;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '6px 8px',
      borderLeft: `2px solid ${meta.color}`,
      background: 'rgba(255,255,255,0.015)',
      fontFamily: 'var(--font-mono)',
      fontSize: 12,
      color: 'var(--text-1)',
      transition: 'background 100ms ease'
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(51,181,229,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.015)'}
    >
      <span style={{ color: 'var(--text-3)', minWidth: 70, letterSpacing: '0.06em' }}>
        {event.time}
      </span>
      <Tooltip title={event.category} placement="top">
        <span style={{
          display: 'inline-block',
          minWidth: 62,
          padding: '2px 6px',
          background: `${meta.color}18`,
          color: meta.color,
          border: `1px solid ${meta.color}44`,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          textAlign: 'center'
        }}>
          {meta.label}
        </span>
      </Tooltip>
      <span style={{ flex: 1 }}>{renderEventBody(event)}</span>
    </div>
  );
};

// ── Horizontal fuel-bar used for hit quality and weapon breakdowns ────────────
const FuelBar = ({ value, max, color, label, sub }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 4, fontFamily: 'var(--font-mono)', fontSize: 11
      }}>
        <span style={{ color: 'var(--text-1)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
          {label}
        </span>
        {sub && <span style={{ color: 'var(--text-3)', fontSize: 10, letterSpacing: '0.1em', flexShrink: 0 }}>{sub}</span>}
        <span style={{ color, fontWeight: 600, minWidth: 52, textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
          {formatNumber(value)}
        </span>
      </div>
      <div className="fuel-bar">
        <span className="fill" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
      </div>
    </div>
  );
};

function LogVisualizer() {
  const [input, setInput] = useState('');
  const [events, setEvents] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [disabledKinds, setDisabledKinds] = useState(() => new Set(DEFAULT_DISABLED_KINDS));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [typeDirection, setTypeDirection] = useState('out');

  const toggleKind = (kind) => {
    setDisabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const handleParse = () => {
    setParseError(null);
    try {
      const parsed = parseLog(input);
      if (parsed.length === 0) {
        setParseError('No recognizable log entries found. Expected EVE Gamelog format: "[ YYYY.MM.DD HH:MM:SS ] (category) Message".');
      }
      setEvents(parsed);
    } catch (err) {
      setParseError(err.message || 'Failed to parse log.');
      setEvents([]);
    }
  };

  const handleClear = () => {
    setInput('');
    setEvents([]);
    setParseError(null);
  };

  const handleSample = () => {
    setInput(SAMPLE_LOG);
  };

  // Filter events by the kinds the user has enabled in the Settings dialog.
  // Disabling noisy/idle event types (Notify, jumps) collapses the timeline to the meaningful window.
  const displayEvents = useMemo(
    () => events.filter((e) => !disabledKinds.has(e.kind)),
    [events, disabledKinds]
  );

  // All distinct kinds present in the parsed input — drives the settings list.
  const presentKinds = useMemo(() => {
    const seen = new Map();
    for (const e of events) {
      seen.set(e.kind, (seen.get(e.kind) || 0) + 1);
    }
    return [...seen.entries()]
      .map(([kind, count]) => ({ kind, count }))
      .sort((a, b) => b.count - a.count);
  }, [events]);

  const stats = useMemo(() => aggregate(displayEvents), [displayEvents]);
  const timeline = useMemo(() => buildTimeline(displayEvents), [displayEvents]);
  const hits = useMemo(() => buildHits(displayEvents), [displayEvents]);
  const t0 = useMemo(() => {
    if (displayEvents.length === 0) return 0;
    return Math.min(...displayEvents.map((e) => e.seconds));
  }, [displayEvents]);

  // Sort events for the timeline display: chronological ascending so the player can read top-to-bottom.
  const sortedEvents = useMemo(
    () => [...displayEvents].sort((a, b) => a.seconds - b.seconds),
    [displayEvents]
  );

  const peakDpsOut = timeline.reduce((max, p) => Math.max(max, p.dpsOut ?? 0), 0);
  const peakDpsIn = timeline.reduce((max, p) => Math.max(max, p.dpsIn ?? 0), 0);

  // Max values for fuel-bar scaling
  const maxQualityOut = Math.max(1, ...stats.byQualityOut.map(q => q.value));
  const maxWeapon = Math.max(1, ...stats.byWeapon.map(w => w.dmg));
  const maxDmgType = Math.max(1, ...stats.byDamageType.map(d => d.value));
  const maxSource = Math.max(1, ...stats.bySource.map(s => s.dmg));

  const dmgTypeColors = ['#33b5e5', '#ffb547', '#5bd99a', '#ff8a65', '#e065ff', '#56697d'];

  // Format engagement duration as mm:ss or hh:mm:ss
  const fmtDuration = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2,'0')}m`;
    if (m > 0) return `${m}m ${String(sec).padStart(2,'0')}s`;
    return `${sec}s`;
  };

  return (
    <>
      <PageHead
        crumb="UTILITIES // COMBAT LOG"
        title="Log Visualizer"
        actions={
          <>
            <Btn
              tone="danger"
              icon={I.Sync}
              onClick={handleClear}
              disabled={!input && events.length === 0}
            >
              Reset
            </Btn>
            <Btn
              tone="primary"
              icon={I.Timeline}
              onClick={handleParse}
              disabled={!input.trim()}
            >
              Parse
            </Btn>
          </>
        }
      />

      {/* ── Input + Summary row ─────────────────────────────────────────────── */}
      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1.4fr' }}>

        {/* Raw Log Input */}
        <Panel
          title="Raw Log Input"
          sub={events.length > 0
            ? (displayEvents.length !== events.length
              ? `${displayEvents.length} / ${events.length} events`
              : `${events.length} events parsed`)
            : undefined}
          action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {events.length > 0 && (
                <Tooltip title="Message type filters">
                  <span>
                    <Btn
                      tone=""
                      onClick={() => setSettingsOpen(true)}
                      disabled={events.length === 0}
                      style={{ padding: '6px 10px', fontSize: 10 }}
                    >
                      Filters
                    </Btn>
                  </span>
                </Tooltip>
              )}
              <Btn tone="" onClick={handleSample} style={{ padding: '6px 10px', fontSize: 10 }}>
                Sample
              </Btn>
            </div>
          }
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={'[ 2026.04.28 09:55:43 ] (combat) 18548 to Sansha\'s Dreadnought - Zorya\'s Supratidal Entropic Disintegrator - Smashes\n[ 2026.04.28 09:55:42 ] (combat) 2285 from Sansha\'s Dreadnought - Smashes'}
            style={{
              width: '100%',
              minHeight: 220,
              maxHeight: 380,
              resize: 'vertical',
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid var(--line-strong)',
              color: 'var(--text-1)',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              lineHeight: 1.55,
              padding: '10px 12px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent)'}
            onBlur={e => e.target.style.borderColor = 'var(--line-strong)'}
          />
          {parseError && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              background: 'rgba(255,91,106,0.08)',
              borderLeft: '2px solid var(--danger)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-1)',
              lineHeight: 1.5
            }}>
              <div style={{ color: 'var(--danger)', letterSpacing: '0.18em', textTransform: 'uppercase', fontSize: 10, marginBottom: 3 }}>
                Parse Error
              </div>
              {parseError}
            </div>
          )}
          {events.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {presentKinds.slice(0, 6).map(({ kind, count }) => {
                const meta = KIND_LABELS[kind] || { label: kind };
                const disabled = disabledKinds.has(kind);
                return (
                  <Chip key={kind} tone={disabled ? 'ghost' : ''} title={`${meta.label}: ${count} events`}>
                    {meta.label.replace(' (outgoing)', ' out').replace(' (incoming)', ' in')}: {count}
                  </Chip>
                );
              })}
            </div>
          )}
        </Panel>

        {/* Engagement Summary */}
        <Panel title="Engagement Summary" sub={events.length > 0 && stats.span > 0 ? fmtDuration(stats.span) : undefined}>
          {events.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center', padding: '40px 0' }}>
              No data — paste a log and click Parse
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <div className="stat-cell ok">
                <div className="label">Dmg Dealt</div>
                <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: 'var(--ok)', marginTop: 4 }}>
                  {formatNumber(stats.totalOut)}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  avg {stats.avgDpsOut.toFixed(1)} dps
                </div>
              </div>

              <div className="stat-cell danger">
                <div className="label">Dmg Taken</div>
                <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: 'var(--danger)', marginTop: 4 }}>
                  {formatNumber(stats.totalIn)}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  avg {stats.avgDpsIn.toFixed(1)} dps
                </div>
              </div>

              <div className="stat-cell">
                <div className="label">Accuracy</div>
                <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: 'var(--accent)', marginTop: 4 }}>
                  {stats.accuracy.toFixed(1)}%
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  {stats.hits}h / {stats.shotsFired}s
                </div>
              </div>

              <div className="stat-cell ok">
                <div className="label">Peak Out DPS</div>
                <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ok)', marginTop: 4 }}>
                  {peakDpsOut.toFixed(1)}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>dps</div>
              </div>

              <div className="stat-cell danger">
                <div className="label">Peak In DPS</div>
                <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--danger)', marginTop: 4 }}>
                  {peakDpsIn.toFixed(1)}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>dps</div>
              </div>

              <div className="stat-cell amber">
                <div className="label">Bounty ISK</div>
                <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: 'var(--amber)', marginTop: 4 }}>
                  {formatISK(stats.bountyTotal)}
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                  {stats.jumps.length > 0 ? `${stats.jumps.length} jumps` : 'isk'}
                </div>
              </div>

              <div className="stat-cell" style={{ gridColumn: 'span 3' }}>
                <div className="label">Duration</div>
                <div className="mono" style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-0)', marginTop: 4 }}>
                  {fmtDuration(stats.span)}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)', marginLeft: 12 }}>
                    {displayEvents.length} events · {stats.jumps.length} jumps
                  </span>
                </div>
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* ── DPS over time chart ─────────────────────────────────────────────── */}
      {events.length > 0 && (
        <Panel
          title="DPS Over Time"
          sub={`${DPS_WINDOW}s window · circles = individual hits · NEUT on right axis (GJ/s)`}
        >
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <ComposedChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(120,180,230,0.08)" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  stroke="var(--text-3)"
                  tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--text-3)' }}
                  tickFormatter={(v) => secondsOfDayToHMS(t0 + v)}
                />
                <YAxis
                  yAxisId="dps"
                  stroke="var(--text-3)"
                  tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--text-3)' }}
                />
                <YAxis
                  yAxisId="neut"
                  orientation="right"
                  stroke="#e065ff"
                  tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: '#e065ff' }}
                />
                <RTooltip content={<ChartTooltip t0={t0} />} cursor={{ stroke: 'rgba(51,181,229,0.15)' }} />
                <Legend
                  wrapperStyle={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em' }}
                />
                <Line
                  yAxisId="dps"
                  type="monotone"
                  dataKey="dpsOut"
                  name="Outgoing DPS"
                  stroke="#5bd99a"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  yAxisId="dps"
                  type="monotone"
                  dataKey="dpsIn"
                  name="Incoming DPS"
                  stroke="#ff5b6a"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  yAxisId="neut"
                  type="monotone"
                  dataKey="neutOut"
                  name="Outgoing NEUT (GJ/s)"
                  stroke="#e065ff"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={false}
                />
                <Line
                  yAxisId="neut"
                  type="monotone"
                  dataKey="neutIn"
                  name="Incoming NEUT (GJ/s)"
                  stroke="#ff8a65"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  dot={false}
                />
                <Scatter
                  yAxisId="dps"
                  name="Outgoing hit"
                  data={hits.out}
                  dataKey="amount"
                  fill="#5bd99a"
                  shape="circle"
                />
                <Scatter
                  yAxisId="dps"
                  name="Incoming hit"
                  data={hits.in}
                  dataKey="amount"
                  fill="#ff5b6a"
                  shape="circle"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      )}

      {/* ── Hit Quality + Damage Breakdown ─────────────────────────────────── */}
      {events.length > 0 && (
        <div className="grid-2">
          {/* Hit Quality (outgoing) */}
          <Panel title="Hit Quality" sub="outgoing shots by result">
            {stats.byQualityOut.length === 0 ? (
              <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                No outgoing hits recorded.
              </div>
            ) : (
              <div>
                {stats.byQualityOut.map((q) => (
                  <FuelBar
                    key={q.name}
                    label={q.name}
                    value={q.value}
                    max={maxQualityOut}
                    color={QUALITY_COLORS[q.name] || '#33b5e5'}
                    sub={maxQualityOut > 0 ? `${((q.value / maxQualityOut) * 100).toFixed(0)}%` : ''}
                  />
                ))}
              </div>
            )}
          </Panel>

          {/* Damage by Source (switchable out/in) */}
          <Panel
            title={typeDirection === 'out' ? 'Damage by Type' : 'Damage by Source'}
            sub={typeDirection === 'out'
              ? 'inferred from weapon name'
              : 'incoming hits grouped by attacker'}
            action={
              <ToggleButtonGroup
                size="small"
                exclusive
                value={typeDirection}
                onChange={(_, v) => v && setTypeDirection(v)}
                sx={{ '& .MuiToggleButton-root': {
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.65rem',
                  letterSpacing: '0.12em',
                  padding: '3px 10px',
                  color: 'var(--text-2)',
                  borderColor: 'var(--line-strong)',
                  '&.Mui-selected': { color: 'var(--accent)', borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
                }}}
              >
                <ToggleButton value="out">Out</ToggleButton>
                <ToggleButton value="in">In</ToggleButton>
              </ToggleButtonGroup>
            }
          >
            {typeDirection === 'out' ? (
              stats.byDamageType.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  No outgoing damage recorded.
                </div>
              ) : (
                <div>
                  {stats.byDamageType.map((d, i) => (
                    <FuelBar
                      key={d.name}
                      label={d.name}
                      value={d.value}
                      max={maxDmgType}
                      color={dmgTypeColors[i % dmgTypeColors.length]}
                      sub={maxDmgType > 0 ? `${((d.value / maxDmgType) * 100).toFixed(0)}%` : ''}
                    />
                  ))}
                  {stats.byWeapon.length > 0 && (
                    <>
                      <div className="divlabel" style={{ marginTop: 14 }}>By Weapon</div>
                      {stats.byWeapon.slice(0, 6).map((w) => (
                        <FuelBar
                          key={w.name}
                          label={w.name}
                          value={w.dmg}
                          max={maxWeapon}
                          color="#33b5e5"
                        />
                      ))}
                    </>
                  )}
                </div>
              )
            ) : (
              stats.bySource.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                  No incoming damage recorded.
                </div>
              ) : (
                <div>
                  {stats.bySource.map((s) => (
                    <FuelBar
                      key={s.name}
                      label={s.name}
                      value={s.dmg}
                      max={maxSource}
                      color="#ff5b6a"
                    />
                  ))}
                </div>
              )
            )}
          </Panel>
        </div>
      )}

      {/* ── Event Timeline ──────────────────────────────────────────────────── */}
      {events.length > 0 && (
        <Panel title="Event Timeline" sub={`${sortedEvents.length} events`}>
          <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, paddingRight: 4 }}>
            {sortedEvents.map((e, idx) => (
              <TimelineRow key={idx} event={e} />
            ))}
          </div>
        </Panel>
      )}

      {/* ── Settings dialog ─────────────────────────────────────────────────── */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ style: { background: 'var(--bg-2)', border: '1px solid var(--line-strong)', color: 'var(--text-0)' } }}
      >
        <DialogTitle style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 14, borderBottom: '1px solid var(--line)' }}>
          Message Type Filters
        </DialogTitle>
        <DialogContent dividers style={{ borderColor: 'var(--line)' }}>
          {presentKinds.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>No events parsed yet.</div>
          ) : (
            <List dense disablePadding>
              {presentKinds.map(({ kind, count }) => {
                const meta = KIND_LABELS[kind] || { label: kind, desc: '' };
                const enabled = !disabledKinds.has(kind);
                return (
                  <ListItem
                    key={kind}
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={enabled}
                        onChange={() => toggleKind(kind)}
                        sx={{ '& .MuiSwitch-thumb': { background: enabled ? 'var(--accent)' : 'var(--text-3)' } }}
                      />
                    }
                    style={{ borderBottom: '1px solid var(--line-soft)' }}
                  >
                    <ListItemText
                      primary={
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em', color: 'var(--text-0)' }}>
                            {meta.label}
                          </span>
                          <Chip tone="ghost">{count}</Chip>
                        </span>
                      }
                      secondary={<span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)' }}>{meta.desc}</span>}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid var(--line)', gap: 8, padding: '12px 16px' }}>
          <Btn tone="" onClick={() => setDisabledKinds(new Set())} disabled={presentKinds.length === 0}>
            Enable All
          </Btn>
          <Btn tone="" onClick={() => setDisabledKinds(new Set(presentKinds.map((k) => k.kind)))} disabled={presentKinds.length === 0}>
            Disable All
          </Btn>
          <Btn tone="primary" onClick={() => setSettingsOpen(false)}>
            Done
          </Btn>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default LogVisualizer;
